import "webdatarocks/webdatarocks.css";
import React, { useEffect, useRef, useState } from "react";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import WebDataRocks from "webdatarocks";
import LoadingRaport from "./LoadingRaport";
import useAssets from "../../../store/ReactQueries/assets/asset/useAsset";
import { DataSelectType } from "../../../models/DataSelectType";
import { AssetInterface } from "../../../models/AssetModel/Asset";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";

const PAGE_SIZE = 100;
const rowsName = [
  "Barcode",
  "Title",
  "Description",
  "Asset Category",
  "Asset Type",
  "Location",
  "Asset Department",
  "Asset Status",
  "Created Date",
  "Updated Date",
];

const FIELD_MAP: Record<string, { field: string; direct: boolean }> = {
  Barcode: { field: "Barcode", direct: true },
  Title: { field: "Title", direct: true },
  "Asset Category": { field: "AssetCategoryId", direct: false },
  "Asset Type": { field: "AssetTypeId", direct: false },
  Location: { field: "LocationId", direct: false },
  "Asset Department": { field: "AssetDepartmentId", direct: false },
  "Asset Status": { field: "AssetStatusId", direct: false },
};

const AssetsRaport = () => {
  const pivotRef = useRef<HTMLDivElement>(null);
  const pivotInstanceRef = useRef<InstanceType<typeof WebDataRocks> | null>(null);
  const assetsRef = useRef<AssetInterface[]>([]);
  const filterHandlerRef = useRef<() => void>(() => {});
  const isUpdatingDataRef = useRef(false);

  const [page, setPage] = useState<number>(1);
  const [filterData, setFilterData] = useState<DataSelectType[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const { darkMode } = useDarkModeStore();

  const { data: assets, isLoading, error: assetErrors } = useAssets(page, PAGE_SIZE, filterData);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filterData]);

  // Always keep filterHandlerRef pointing at the latest closure so the
  // WebDataRocks event listener (registered once) never goes stale.
  filterHandlerRef.current = () => {
    // Skip when we are the ones pushing new data — not a user filter action
    if (isUpdatingDataRef.current || !pivotInstanceRef.current) return;
    const report = pivotInstanceRef.current.getReport() as WebDataRocks.Report;
    console.log("[AssetsRaport] reportchange fired", JSON.stringify(report.slice?.rows?.map(r => ({ name: r.uniqueName, filter: r.filter })), null, 2));
    const activeFilters: DataSelectType[] = [];

    report.slice?.rows?.forEach((row) => {
      const name = row.uniqueName ?? "";
      const members = row.filter?.members;
      if (!members || members.length === 0) return;

      // Members are prefixed with the hierarchy name e.g. "Asset Category.Mobilje" → strip to "Mobilje"
      const rawValue = members[0];
      const displayValue = rawValue.includes(".") ? rawValue.substring(rawValue.indexOf(".") + 1) : rawValue;
      const mapping = FIELD_MAP[name];
      if (!mapping) return;

      let value = displayValue;

      if (!mapping.direct) {
        const found = assetsRef.current.find((a) => {
          if (name === "Asset Category") return a.assetCategory?.title === displayValue;
          if (name === "Asset Type") return a.assetType?.title === displayValue;
          if (name === "Location") return a.location?.title === displayValue;
          if (name === "Asset Department") return a.assetDepartment?.title === displayValue;
          if (name === "Asset Status") return a.assetStatus?.title === displayValue;
          return false;
        });
        if (!found) return;
        const idMap: Record<string, string | undefined> = {
          "Asset Category": found.assetCategoryId,
          "Asset Type": found.assetTypeId,
          Location: found.locationId,
          "Asset Department": found.assetDepartmentId,
          "Asset Status": found.assetStatusId,
        };
        value = idMap[name] ?? displayValue;
      }

      activeFilters.push({ field: mapping.field, label: name, value });
    });

    console.log("[AssetsRaport] activeFilters extracted:", activeFilters);
    setPage(1);
    setFilterData(activeFilters.length > 0 ? activeFilters : undefined);
  };

  useEffect(() => {
    console.log("[AssetsRaport] effect ran", { hasPivotRef: !!pivotRef.current, hasAssets: !!assets, isLoading, hasError: !!assetErrors });
    if (!pivotRef.current || !assets || isLoading || assetErrors) return;
    console.log("[AssetsRaport] passed guard, assets:", assets.length, "pivotInstance:", !!pivotInstanceRef.current);

    assetsRef.current = assets;
    setLoading(true);

    const assetsData = assets.map((asset) => ({
      Barcode: `${asset.barcode.toString()}`,
      Title: asset.title,
      Description: asset.description,
      "Asset Category": asset.assetCategory?.title,
      "Asset Type": asset.assetType?.title,
      Location: asset.location?.title,
      "Asset Department": asset.assetDepartment?.title,
      "Asset Status": asset.assetStatus?.title || "",
      "Created Date": asset.createdDate && new Date(asset.createdDate).toLocaleDateString(),
      "Updated Date": asset.updatedDate && new Date(asset.updatedDate).toLocaleDateString(),
    }));

    if (!pivotInstanceRef.current) {
      pivotInstanceRef.current = new WebDataRocks({
        container: pivotRef.current,
        toolbar: true,
        height: "100%",
        width: "100%",
        report: {
          dataSource: { data: assetsData },
          options: {
            grid: {
              type: "flat",
              showGrandTotals: "off",
              showTotals: "off",
            },
          },
          slice: {
            rows: rowsName.map((uniqueName) => ({ uniqueName })),
            columns: [],
            measures: [],
          },
        },
        reportcomplete: () => {
          // Register here — pivot is fully initialised at this point
          pivotInstanceRef.current?.on("reportchange", () => filterHandlerRef.current());
          console.log("[AssetsRaport] reportchange listener registered");
          setTimeout(() => setLoading(false), 500);
        },
      });
    } else {
      // Flag prevents reportchange from treating this data push as a user filter action
      isUpdatingDataRef.current = true;
      pivotInstanceRef.current.updateData({ data: assetsData });
      setTimeout(() => { isUpdatingDataRef.current = false; }, 0);
      setLoading(false);
    }
  }, [assets, isLoading, assetErrors, darkMode]);

  return (
    <div className="mb-4" style={{ height: "83vh", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {loading && <LoadingRaport />}
        <div ref={pivotRef} style={{ height: "100%", width: "100%" }} />
      </div>

      {/* Pagination bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <Button size="sm" variant="outlined" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <Typography level="body-sm">Page {page}</Typography>
        <Button
          size="sm"
          variant="outlined"
          disabled={!assets || assets.length < PAGE_SIZE}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default AssetsRaport;
